import { useCallback, useEffect, useState, useRef, lazy, Suspense } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { usePendingConsents, useAcceptConsent } from '@/hooks/usePendingConsents';
import { useEmulationGuard } from '@/hooks/useEmulationGuard';
import { captureError } from '@/lib/sentry';
import type { PendingConsent } from '@/types/legal-consent';

// Lazy-loaded so users who only see click-to-accept docs never pay the
// react-signature-canvas bundle cost.
const SignatureCanvas = lazy(() =>
  import('react-signature-canvas').then((m) => ({ default: m.default })),
);

// Non-dismissible Dialog props, factored out to keep the JSX branches readable
// and to ensure the error branch carries the exact same guards as the consent
// branch (refusing to load consents should never let the user proceed).
const NON_DISMISSIBLE = {
  open: true,
  onOpenChange: () => undefined,
} as const;

const NON_DISMISSIBLE_CONTENT_PROPS = {
  onEscapeKeyDown: (e: Event) => e.preventDefault(),
  onPointerDownOutside: (e: Event) => e.preventDefault(),
  onInteractOutside: (e: Event) => e.preventDefault(),
} as const;

export function ConsentModal() {
  const { signOut } = useAuth();
  const { toast } = useToast();
  const { data: pending, isLoading, error } = usePendingConsents();
  const acceptConsent = useAcceptConsent();
  const { isEmulating, blockIfEmulating } = useEmulationGuard();
  const [currentIndex, setCurrentIndex] = useState(0);

  // signOut() is async and can reject (network failure, Supabase outage). The
  // modal is the user's only escape hatch from a stuck consent gate; a silent
  // signOut failure would leave them trapped with no telemetry.
  const safeSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (err) {
      captureError(err as Error, { feature: 'consent-modal-signout' });
      toast({
        title: 'Sign-out failed',
        description: 'Could not sign you out. Please try again or close this tab.',
        variant: 'destructive',
      });
    }
  }, [signOut, toast]);
  // TODO: replace `any` with `ReactSignatureCanvas` from
  // `@types/react-signature-canvas`. Direct `useRef<ReactSignatureCanvas>` fails
  // because the default export's type isn't surfaced through React.lazy's
  // dynamic-import inference. Revisit when @types/react-signature-canvas
  // exposes a ForwardRefExoticComponent default.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sigRef = useRef<any>(null);
  const [hasSigned, setHasSigned] = useState(false);

  // Reset to the first item whenever the head of the pending list changes.
  // Keying on length alone would miss the case where length stays equal but
  // the first item's identity changes (e.g., an admin republished the top
  // doc mid-session, or the server returned the same docs in a different
  // order).
  const headVersionId = pending?.[0]?.version_id;
  useEffect(() => {
    setCurrentIndex(0);
    setHasSigned(false);
  }, [headVersionId]);

  // Tail-truncation guard: if `pending` shrinks (e.g., admin retracts a doc
  // mid-session) such that `currentIndex` exceeds the new bounds, clamp back
  // to 0. Otherwise `pending[currentIndex]` is undefined and the modal would
  // return null — silently bypassing the consent gate.
  useEffect(() => {
    if (pending && currentIndex >= pending.length && pending.length > 0) {
      setCurrentIndex(0);
    }
  }, [pending, currentIndex]);

  // Skip the consent gate entirely during a superadmin emulation. Two reasons:
  //   1) Radix Dialog with modal={true} attaches a document-level pointerdown
  //      listener in capture phase. When this modal is open and the admin
  //      clicks ImpersonationBanner's "Stop emulating" button, that listener
  //      fires first and calls stopImmediatePropagation via
  //      onPointerDownOutside.preventDefault — so the banner button is
  //      visually present but unclickable. Admin gets trapped.
  //   2) Even if (1) were solved, the Iron Rule (legal-docs SKILL.md #5)
  //      already forbids accepting consent on behalf of a user. The
  //      accept-consent edge fn refuses; the modal would just be there to
  //      look at. The legitimate place to view a target's consent state is
  //      the Legal Agreements card in the superadmin user-detail panel.
  //
  // This early return MUST come after every hook above. Previously it ran at
  // the top of the component, which violated the Rules of Hooks: when the
  // admin clicked "Start emulation", `isEmulating` flipped false→true between
  // renders, the conditional skipped useCallback/useRef/useState/useEffect,
  // and React threw error #310 ("Rendered fewer hooks than expected"). The
  // same violation in reverse fired on Stop emulating. The downstream churn
  // surfaced in the parent tree as React #300 (Max update depth).
  if (isEmulating) return null;

  // Error state: render a blocking modal with a refresh affordance. We must
  // NOT return null on error — that would let the user proceed past a
  // legally-required gate while the read path was broken.
  if (error) {
    return (
      <Dialog {...NON_DISMISSIBLE}>
        <DialogContent
          className="max-w-md [&>button]:hidden"
          {...NON_DISMISSIBLE_CONTENT_PROPS}
        >
          <DialogHeader>
            <DialogTitle>Couldn't load required legal documents</DialogTitle>
            <DialogDescription>
              We couldn't reach the consent service. Please refresh the page. If
              this keeps happening, sign out and try again, or contact support.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={safeSignOut}>
              Sign out
            </Button>
            <Button onClick={() => window.location.reload()}>Refresh</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Still loading first response, or there's nothing pending — render nothing.
  if (isLoading || !pending || pending.length === 0) return null;

  const current: PendingConsent | undefined = pending[currentIndex];
  if (!current) return null;

  const handleClickAccept = () => {
    if (blockIfEmulating('Accepting legal consent')) return;
    // Double-click guard. React's disabled state isn't synchronous with click
    // dispatch on slow devices / held Enter, so the button-disabled prop alone
    // can let a second mutation through. The server's idempotent unique-key
    // path covers the duplicate, but suppressing here avoids the toast spam
    // and the race with invalidateQueries.
    if (acceptConsent.isPending) return;
    acceptConsent.mutate({
      version_id: current.version_id,
      signature_type: 'click',
    });
  };

  const handleDigitalAccept = () => {
    if (blockIfEmulating('Accepting legal consent')) return;
    if (acceptConsent.isPending) return;
    const dataUrl = sigRef.current?.toDataURL?.();
    if (!dataUrl) {
      // Should be unreachable: the button is disabled until `hasSigned` is true,
      // and toDataURL is part of the canvas's public API. If we land here, the
      // canvas library shape changed underneath us — surface it loudly.
      captureError(new Error('SignatureCanvas.toDataURL returned no data'), {
        feature: 'consent-modal-digital-accept',
      });
      return;
    }
    acceptConsent.mutate({
      version_id: current.version_id,
      signature_type: 'digital',
      signature_data: { dataUrl },
    });
  };

  return (
    <Dialog {...NON_DISMISSIBLE}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col [&>button]:hidden"
        {...NON_DISMISSIBLE_CONTENT_PROPS}
      >
        <DialogHeader>
          <DialogTitle>{current.name}</DialogTitle>
          <DialogDescription>
            Version {current.version} — please review and accept to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4 text-base leading-relaxed">
          {/* react-markdown 10.x filters javascript:/data: URIs by default.
              Do NOT add rehype-raw — it would re-enable inline HTML/scripts
              and turn admin-authored content into an XSS surface. */}
          {/* Explicit components prop replaces the older `prose prose-sm`
              wrapping. Tailwind Typography defaults were too tight for legal
              prose; controlling each element directly lets us give paragraphs,
              headings, lists, and blockquotes the breathing room they need to
              read like an actual document instead of one giant block of text. */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children, ...props }) => (
                <h1 className="text-2xl font-bold mt-2 mb-5 pb-3 border-b" {...props}>{children}</h1>
              ),
              h2: ({ children, ...props }) => (
                <h2 className="text-xl font-bold mt-10 mb-4" {...props}>{children}</h2>
              ),
              h3: ({ children, ...props }) => (
                <h3 className="text-lg font-semibold mt-7 mb-3" {...props}>{children}</h3>
              ),
              h4: ({ children, ...props }) => (
                <h4 className="text-base font-semibold mt-5 mb-2" {...props}>{children}</h4>
              ),
              p: ({ children, ...props }) => (
                <p className="my-4 leading-relaxed" {...props}>{children}</p>
              ),
              blockquote: ({ children, ...props }) => (
                <blockquote
                  className="my-5 border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 italic [&_p]:my-2"
                  {...props}
                >
                  {children}
                </blockquote>
              ),
              ul: ({ children, ...props }) => (
                <ul className="list-disc pl-6 my-4 space-y-1.5" {...props}>{children}</ul>
              ),
              ol: ({ children, ...props }) => (
                <ol className="list-decimal pl-6 my-4 space-y-1.5" {...props}>{children}</ol>
              ),
              li: ({ children, ...props }) => (
                <li className="leading-relaxed" {...props}>{children}</li>
              ),
              hr: () => <hr className="my-10 border-t-2 border-muted" />,
              table: ({ children, ...props }) => (
                <div className="my-6 overflow-x-auto rounded border">
                  <table className="w-full border-collapse text-sm" {...props}>{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
              th: ({ children, ...props }) => (
                <th className="border-b px-3 py-2 text-left font-semibold" {...props}>{children}</th>
              ),
              td: ({ children, ...props }) => (
                <td className="border-t px-3 py-2 align-top" {...props}>{children}</td>
              ),
              strong: ({ children, ...props }) => (
                <strong className="font-semibold" {...props}>{children}</strong>
              ),
              a: ({ children, href, ...props }) => (
                <a
                  href={href}
                  className="text-blue-600 dark:text-blue-400 underline underline-offset-2"
                  rel="noopener noreferrer nofollow"
                  {...props}
                >
                  {children}
                </a>
              ),
            }}
          >
            {current.content}
          </ReactMarkdown>
        </div>

        <DialogFooter className="flex-col items-stretch gap-3 sm:flex-col">
          {current.signature_type === 'digital' ? (
            <>
              <Suspense fallback={<div className="h-32 bg-muted rounded" />}>
                <div className="border rounded bg-background">
                  <SignatureCanvas
                    ref={sigRef}
                    onEnd={() => setHasSigned(true)}
                    canvasProps={{ className: 'w-full h-32' }}
                  />
                </div>
              </Suspense>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  onClick={safeSignOut}
                  disabled={acceptConsent.isPending}
                >
                  Sign out
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    sigRef.current?.clear?.();
                    setHasSigned(false);
                  }}
                  disabled={acceptConsent.isPending}
                >
                  Clear
                </Button>
                <Button
                  onClick={handleDigitalAccept}
                  disabled={!hasSigned || acceptConsent.isPending || isEmulating}
                  title={isEmulating ? 'Cannot accept consent while a superadmin is emulating this user.' : undefined}
                >
                  {acceptConsent.isPending ? 'Recording…' : 'I Accept & Sign'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={safeSignOut}
                disabled={acceptConsent.isPending}
              >
                Sign out
              </Button>
              <Button
                onClick={handleClickAccept}
                disabled={acceptConsent.isPending || isEmulating}
                title={isEmulating ? 'Cannot accept consent while a superadmin is emulating this user.' : undefined}
              >
                {acceptConsent.isPending ? 'Recording…' : 'I Accept'}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
